// If devModeEnabled() === true: Saves to localStorage on every `setItem`.
// Enables manually tweaking localStorage values and seeing the changes in the real time.
import { devModeEnabled } from "./devMode";
import { inRange } from "./util";

// BusinessLogic
export const FIELD_FEATURE_MATRIX_SET = "feature_matrix_set";
export const FIELD_FEATURES_MANUFACTURING = "features-manufacturing";
export const FIELD_FEATURES_BASIC = "features-basic";
export const FIELD_SYS_FLAG_ENV = "sys-flags-env";
export const FIELD_SYS_FLAG_LANG = "sys-flags-lang";
export const FIELD_SYS_FLAG_INTEGRATION = "sys-flags-integration";

// Auth
export const FIELD_ROLES = "user.roles";
export const FIELD_USER_TITLE = "user.title";
export const FIELD_USER_JSON = "user.json";
export const FIELD_USER_LANG = "user.lang";
export const FIELD_ORG_TITLE = "user.org_title";
export const FIELD_ORG_UUID = "user.org_uuid";
export const FIELD_ORG_CODE = "user.org_code";
export const FIELD_ORG_ALLOWS_PIN = "user.org_allows_pin";
export const FIELD_USERNAME = "user.username";
export const FIELD_USER_UUID = "user.uuid";
export const FIELD_USER_PLANT = "user.plant";
export const FIELD_USER_PLANTS = "user.plants";
export const FIELD_USER_LINE_GROUP = "user.line_group";
export const FIELD_USER_LINE_GROUPS = "user.line_groups";
export const FIELD_USER_TAGS = "user.tags";
export const FIELD_HOMEPAGE_DASHBOARD = "user.homepage_dashboard";
export const FIELD_USER_MUST_RESET = "user.must_reset";
export const FIELD_USER_MUST_ACTIVATE_TWOFA = "user.must_activate_twofa";
export const FIELD_JWT_TOKEN = "jwtToken";
export const FIELD_REDIRECT_URL = "login-redirect-url";
export const FIELD_LAST_SEEN_VERSION = "user.last-seen-version";
export const FIELD_NOTIFIED_VERSION = "user.notified-version";
export const FIELD_USER_TICKETS = "user.tickets";

// LargeDataTable
export const FIELD_HIDDEN_COLUMNS = "hidden-columns";
export const FIELD_TIME_WINDOW = "time-window";

// MaterialStockForecastContainer
export const FIELD_TIME_GRANULARITY = "planning-table.material-stock-forecast.time-granularity";

// Insights
export const FIELD_INSIGHTS_SELECTION_SHOWCHECKBOXES = "insights-selection-showcheckboxes";
export const FIELD_INSIGHTS_SELECTION_LINEUUIDS = "insights-selection-lineuuids";
export const FIELD_INSIGHTS_SELECTION_EVENTTYPES = "insights-selection-eventtypes";
export const FIELD_INSIGHTS_SELECTION_LINEUUIDS_WIP = "insights-selection-lineuuids-wip";
export const FIELD_INSIGHTS_SELECTION_EVENTTYPES_WIP = "insights-selection-eventtypes-wip";

// planningTable
export const FIELD_PLANNING_TABLE_DAY_WIDTH_PREFIX = "planning-table-day-width-";
export const FIELD_SHOW_HISTORY_BUTTON_PREFIX = "show-history-button-";
export const FIELD_PLANNING_TABLE_REPORT_PREFIX = "planning-table-report-";

// Commonly used constants across multiple files
export const FIELD_LAST_PLANT = "last-plant";
export const FIELD_LAST_LINEGROUP = "last-linegroup";
export const FIELD_DEVICE_UUID = "device_uuid";
export const FIELD_FIXED_QUICK_SIM_CACHE_HASH = "fixed_quick_sim_cache_hash";
export const FIELD_SHOW_PLANNING_DETAILS_FILTER = "show_planning_details_filter";
export const FIELD_PLANNING_DETAILS_POSITION = "planning_details_position";
export const FIELD_PLANNING_DETAILS_WIDTH_RATIO = "planning_details_width";
export const FIELD_PLANNING_DETAILS_HEIGHT_RATIO = "planning_details_height";
export const FIELD_GANTT_PANES_HEIGHTS = "gantt_panes_heights";
export const FIELD_INSIGHTS_TYPES = "insights-types";
export const FIELD_INSIGHTS_PRINT_NUMBER_OF_DAYS = "insights-print-number-of-days";
export const FIELD_PERFORMANCE_MEASUREMENT_SAVED = "performance-measurement-saved";
export const FIELD_RENDERS_VIEW_LAST_MEASUREMENT = "renders-view-last-measurement";
export const FIELD_MUTATIONS_VIEW_LAST_MEASUREMENT = "mutations-view-last-measurement";
export const FIELD_PEOPLE_SCHEDULING_PARAMS = "preplanning.people-scheduling-params";
export const FIELD_IS_TIME_SERIES_VISIBLE = "preplanning.is-time-series-visible";
export const FIELD_AUTOMATIC_SCHEDULING_PARAMS = "shift-board.automatic-scheduling-params";
export const FIELD_ALLOW_USAGE_OF_SAFETY_STOCK = "stock-forecast.allow-usage-of-safety-stock";
export const FIELD_MICROPLAN_ORDER_TYPES = "microplan.order-types";
export const FIELD_ORDERS_HEIGHT_RATIO = "resize_orders_lol";
export const FIELD_SHOW_STOCK_REQUIREMENTS = "planning_table_show_stock_requirements";
export const FIELD_SHOW_UNSCHEDULED_ORDERS = "planning_table_show_unscheduled_orders";
export const FIELD_SHIFT_ASSIGNMENT_WEEK_POOL_PRINT = "shift-assignment-week-pool-print";

class LocalSettings {
    data: Record<string, string> = {};
    inited = false;

    constructor() {
        this.init();
    }

    loadAll(): Record<string, string> {
        const saved: Record<string, string> = {};
        let key: string | null = null;
        let index = 0;

        do {
            key = localStorage.key(index);
            if (key) {
                saved[key] = localStorage.getItem(key) ?? "";
            }
            index += 1;
        } while (key);

        return saved;
    }

    init() {
        this.data = this.loadAll();

        if (this.data === null || typeof this.data !== "object") {
            this.data = {};
        }

        this.inited = true;
        this.log("LocalSettings: loaded");
    }

    saveAll() {
        // In the case we changed the value manually, we don't want to overwrite it
        if (devModeEnabled()) {
            return;
        }

        localStorage.clear();

        for (const [key, value] of Object.entries(this.data)) {
            localStorage.setItem(key, value);
        }
        this.log("LocalSettings: saved");
    }

    setItem(key: string, value: string) {
        this.data[key] = value;
        if (devModeEnabled()) {
            localStorage.setItem(key, value);
        }
        this.log(`LocalSettings: item set: ${key} = ${value}`);
    }

    getItem(key: string): string | null {
        if (!this.inited) {
            console.error("LocalSettings: getItem called before init, key:", key);
            return null;
        }
        const value = Object.prototype.hasOwnProperty.call(this.data, key) ? this.data[key] : null;
        this.log(`LocalSettings: item get: ${key} = ${String(value)}`);
        return value;
    }

    /**
     * Gets a local storage value and converts it to a number if in the specified range.
     * If the value is not a number or not in the range, returns null.
     */
    getNumberIfInRange(key: string, min: number, max: number): number | null {
        const value = this.getItem(key);
        if (!value) {
            return null;
        }

        const value_number = Number(value);
        if (Number.isNaN(value_number)) {
            return null;
        }

        return inRange(value_number, min, max) ? value_number : null;
    }

    getParsed(key: string): Object | null {
        try {
            return JSON.parse(this.getItem(key) ?? "");
        } catch (err) {
            return null;
        }
    }

    removeItem(key: string) {
        delete this.data[key];
        if (devModeEnabled()) {
            localStorage.removeItem(key);
        }
        this.log(`LocalSettings: item removed: ${key}`);
    }

    log(...messages: string[]) {
        if (devModeEnabled()) {
            console.log(...messages);
        }
    }
}

export default new LocalSettings();
